<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Naming Your Core Annual Programs</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Core Annual Programs are the primary, ongoing activities or services your organization offers each year. These programs are at the heart of your organization's work and should align with your mission and aspirational vision. Being able to describe these programs succinctly will be helpful in</p>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Enter up to ten (10) Core Annual Programs and a brief description (up to 85 words) of the activities or services your organization currently administers annually.</p>

            <div class="mt-6">
                <h4 class="text-[1rem] leading-[1.5rem] mb-[0.5rem] font-semibold">Programs <span class="text-[#6B7280]">(10 Programs Max)</span></h4>

                <!-- Core Programs Container -->
                <div class="mt-4">
                    <!-- Core Program Item -->
                    <div
                        v-for="(program, idx) in coreProgramState.corePrograms"
                        :key="idx"
                        class="bg-[#F5F6FA] rounded-md overflow-hidden border border-[#00423D] mt-4"
                        :class="{'border-red-400 shakeX': !isCoreProgramsValid.isValid && (!program.program_name?.trim() || !program.program_description?.trim())}"
                    >
                        <!-- Program Header -->
                        <div class="py-2 px-4 flex items-center justify-between gap-4 flex-wrap">
                            <div class="flex flex-1 items-center gap-2">
                                <div class="flex flex-1 flex-col">
                                    <label for="program-name" class="text-sm text-[#00423D] mb-2">
                                        Program Name
                                    </label>
                                    <input
                                        id="program-name"
                                        v-model="program.program_name"
                                        type="text"
                                        class="w-full px-2 py-2 bg-transparent outline-none focus:outline-none transition-colors duration-200"
                                        placeholder="Write Program Name here..."
                                        @input="onProgramNameInput"
                                    />
                                </div>
                                <button class="p-1 self-end" @click="onRemoveCoreWorkPoint" :data-point-id="idx">
                                    <svg class="w-6 h-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <mask id="mask0_1675_29390" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                            <rect x="0.0527344" y="0.480957" width="48" height="48" fill="#D9D9D9"/>
                                        </mask>
                                        <g mask="url(#mask0_1675_29390)">
                                            <path d="M13.1027 42.481C12.2694 42.481 11.5611 42.1893 10.9777 41.606C10.3944 41.0226 10.1027 40.3143 10.1027 39.481V10.981H8.05273V7.98096H17.4527V6.48096H30.6527V7.98096H40.0527V10.981H38.0027V39.481C38.0027 40.281 37.7027 40.981 37.1027 41.581C36.5027 42.181 35.8027 42.481 35.0027 42.481H13.1027ZM35.0027 10.981H13.1027V39.481H35.0027V10.981ZM18.4027 35.181H21.4027V15.231H18.4027V35.181ZM26.7027 35.181H29.7027V15.231H26.7027V35.181Z" class="fill-[#6B7280]"/>
                                        </g>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Program Description -->
                        <div class="px-4 py-3 border-t border-t-gray-200 bg-white">
                            <textarea
                                class="w-full px-2 py-2 bg-transparent outline-none focus:outline-none transition-colors duration-200 resize-none"
                                rows="2"
                                placeholder="Write Program Description here..."
                                @input="onCoreWorkPointInput"
                                v-model="program.program_description"
                                :data-program-index="idx"
                            ></textarea>
                            <p :class="{ 'text-red-500': program.wordCount > 85 }" class="text-sm mt-2">
                                {{ program.wordCount }} / 85 words
                            </p>
                        </div>
                    </div>

                    <!-- Validation Message -->
                    <transition name="answer-err">
                        <p class="pl-2 mt-1.5 text-sm text-red-400" v-if="!isCoreProgramsValid.isValid && showValidationError">
                            {{ isCoreProgramsValid.issue === "empty"
                                ? "Please Add At Least One Core Program!"
                                : "Make Sure To Include Program Name And Description!"
                            }}
                        </p>
                    </transition>

                    <!-- Add Program Button -->
                    <button
                        v-if="coreProgramState.corePrograms.length < 10"
                        class="flex items-center text-white bg-[#00423D] py-2 px-4 rounded-lg mt-4 hover:bg-[#009C87] transition-colors duration-200"
                        @click="onAddCoreProgram"
                    >
                        <svg class="h-6 w-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id="mask0_1409_29353" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                <rect x="0.322266" y="0.00683594" width="48" height="48" fill="#D9D9D9"/>
                            </mask>
                            <g mask="url(#mask0_1409_29353)">
                                <path d="M24.3223 38.0068C23.8889 38.0068 23.5306 37.8652 23.2473 37.5818C22.9639 37.2985 22.8223 36.9402 22.8223 36.5068V25.5068H11.8223C11.3889 25.5068 11.0306 25.3652 10.7473 25.0818C10.4639 24.7985 10.3223 24.4402 10.3223 24.0068C10.3223 23.5735 10.4639 23.2152 10.7473 22.9318C11.0306 22.6485 11.3889 22.5068 11.8223 22.5068H22.8223V11.5068C22.8223 11.0735 22.9639 10.7152 23.2473 10.4318C23.5306 10.1485 23.8889 10.0068 24.3223 10.0068C24.7556 10.0068 25.1139 10.1485 25.3973 10.4318C25.6806 10.7152 25.8223 11.0735 25.8223 11.5068V22.5068H36.8223C37.2556 22.5068 37.6139 22.6485 37.8973 22.9318C38.1806 23.2152 38.3223 23.5735 38.3223 24.0068C38.3223 24.4402 38.1806 24.7985 37.8973 25.0818C37.6139 25.3652 37.2556 25.5068 36.8223 25.5068H25.8223V36.5068C25.8223 36.9402 25.6806 37.2985 25.3973 37.5818C25.1139 37.8652 24.7556 38.0068 24.3223 38.0068Z" class="fill-white"/>
                            </g>
                        </svg>
                        <span class="pointer-events-none ml-2">Add Program</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Confirmation Dialog -->
        <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 class="text-lg font-semibold mb-4">Confirm Deletion</h3>
                <p class="text-gray-600 mb-6">Are you sure you want to delete this program?</p>
                <div class="flex justify-end gap-4">
                    <button
                        @click="onCancelRemove"
                        class="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        @click="onConformRemove"
                        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from "@/stores/auth";
import { useUserProjectsStore } from "@/stores/projects";
import global from "@/global";

// State management
const coreProgramState = ref({
    corePrograms: []
});

const isCoreProgramsValid = ref({
    isValid: true,
    issue: null
});

const isLoading = ref(false);
const bodyText = ref('');
const retrievedUserAnswer = ref(null);
const conformationPopupIsOpen = ref(false);
const conformDeletionState = ref(null);

// Add new ref for controlling error message visibility
const showValidationError = ref(false);

// Store instances
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();

// Methods
const onAddCoreProgram = () => {
    if (coreProgramState.value.corePrograms.length < 10) {
        coreProgramState.value.corePrograms.push({
            program_name: '',
            program_description: '',
            wordCount: 0
        });
        
        // Dispatch event for immediate update
        window.dispatchEvent(new CustomEvent('core-programs-updated', {
            detail: { programs: coreProgramState.value.corePrograms }
        }));
    }
};

const onRemoveCoreWorkPoint = (event) => {
    const idx = event.currentTarget.dataset.pointId;
    conformationPopupIsOpen.value = true;
    conformDeletionState.value = idx;
};

const onCancelRemove = () => {
    conformationPopupIsOpen.value = false;
    conformDeletionState.value = null;
};

const onConformRemove = async () => {
    if (conformDeletionState.value === null) return;
    
    const idx = conformDeletionState.value;
    const programToDelete = coreProgramState.value.corePrograms[idx];
    
    try {
        // If the program has an ID, delete it from the API
        if (programToDelete.id) {
            const res = await fetch(`${global.API_URL}/core-programs/${programToDelete.id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${authStore.user.accessToken}`,
                },
            });
            
            if (!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }
            
            const data = await res.json();
            if (!data.status) {
                throw new Error('Failed to delete program');
            }
        }
        
        // Remove from local state
        coreProgramState.value.corePrograms.splice(idx, 1);
        
        // Update localStorage with the updated core programs
        localStorage.setItem('corePrograms', JSON.stringify(coreProgramState.value.corePrograms));
        
        // Dispatch event for immediate update
        window.dispatchEvent(new CustomEvent('core-programs-updated', {
            detail: { programs: coreProgramState.value.corePrograms }
        }));
        
        // Notify the store that a question has been updated
        projectsStore.setQuestionUpdate();
    } catch (error) {
        console.error("Failed to delete core program:", error);
    } finally {
        conformationPopupIsOpen.value = false;
        conformDeletionState.value = null;
    }
};

const onCoreWorkPointInput = (event) => {
    const idx = event.currentTarget.dataset.programIndex;
    const text = event.target.value;
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    coreProgramState.value.corePrograms[idx].wordCount = words.length;
    
    // Hide validation error when user starts typing
    showValidationError.value = false;
};

// Add handler for program name input
const onProgramNameInput = () => {
    showValidationError.value = false;
};

const validatePrograms = () => {
    if (coreProgramState.value.corePrograms.length === 0) {
        isCoreProgramsValid.value = {
            isValid: false,
            issue: 'empty'
        };
        showValidationError.value = true;
        return;
    }

    const hasInvalidProgram = coreProgramState.value.corePrograms.some(program => 
        !program.program_name?.trim() || 
        !program.program_description?.trim() ||
        program.wordCount > 85
    );

    isCoreProgramsValid.value = {
        isValid: !hasInvalidProgram,
        issue: hasInvalidProgram ? 'incomplete' : null
    };
    
    if (hasInvalidProgram) {
        showValidationError.value = true;
    }
};

// API Integration
async function fetchCorePrograms() {
    try {
        const res = await fetch(`${global.API_URL}/core-programs`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.corePrograms && data.corePrograms.length > 0) {
            coreProgramState.value.corePrograms = data.corePrograms;
            retrievedUserAnswer.value = data.corePrograms;
            
            // Update localStorage with the fetched core programs
            localStorage.setItem('corePrograms', JSON.stringify(data.corePrograms));
            
            // Calculate word count for each program
            coreProgramState.value.corePrograms.forEach(program => {
                program.wordCount = program.program_description
                    .split(/\s+/)
                    .filter(word => word !== "").length;
            });

            // Emit event to notify that core programs have been updated
            window.dispatchEvent(new CustomEvent('core-programs-updated', {
                detail: { programs: data.corePrograms }
            }));
        }
    } catch (error) {
        console.error("Failed to fetch core programs:", error);
    }
}

// Add event emitter for program updates
const emitProgramUpdate = () => {
    window.dispatchEvent(new CustomEvent('core-programs-updated', {
        detail: { programs: coreProgramState.value.corePrograms }
    }));
};

// Update the save function to emit the event
const saveCorePrograms = async () => {
    try {
        const res = await fetch(`${global.API_URL}/core-programs`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                corePrograms: coreProgramState.value.corePrograms,
            }),
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.status) {
            // Fetch the updated list of programs after successful save
            await fetchCorePrograms();
            // Update localStorage with the latest programs
            localStorage.setItem('corePrograms', JSON.stringify(coreProgramState.value.corePrograms));
            emitProgramUpdate();
        }
    } catch (error) {
        console.error("Failed to save core programs:", error);
    }
};

// Use the validation composable with custom validation
useStepValidation({
    validateFn: async () => {
        // Only validate when next button is clicked
        validatePrograms();
        if (isCoreProgramsValid.value.isValid) {
            return await saveCorePrograms();
        }
        return false;
    }
});

onMounted(async () => {
    isLoading.value = true;
    await fetchCorePrograms();
    isLoading.value = false;
});
</script>

<style scoped>
.answer-err-enter-active,
.answer-err-leave-active {
    transition: opacity 0.3s ease;
}

.answer-err-enter-from,
.answer-err-leave-to {
    opacity: 0;
}

.shakeX {
    animation: shakeX 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shakeX {
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }
    20%, 80% {
        transform: translate3d(2px, 0, 0);
    }
    30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
    }
    40%, 60% {
        transform: translate3d(4px, 0, 0);
    }
}
</style> 